import createClient from 'openapi-fetch';
import type { Client } from 'openapi-fetch';
import type { paths } from '../../types/api/generated';

let cachedClient: Client<paths> | null = null;
let isDemoApiSession = false;
let hasWarnedAboutMissingDemoUrl = false;

/**
 * Marks the session as belonging to the app-review demo user, whose requests
 * are served by a separate backend deployment. Discards the cached client
 * when the mode changes so the next ffhClient() call rebuilds it against the
 * newly resolved base URL.
 *
 * Browser-only: the flag lives in module scope, which a Next.js server
 * process shares across concurrent requests, so flipping it there would
 * leak one user's routing into another's. Server-side callers must resolve
 * the demo state per request instead.
 *
 * @returns true when the demo mode changed as a result of this call
 */
export const setIsDemoApiSession = (isDemoSession: boolean): boolean => {
  if (typeof window === 'undefined') {
    console.warn(
      'setIsDemoApiSession is browser-only and was ignored on the server; module-scoped routing state would leak across requests.'
    );
    return false;
  }
  if (isDemoApiSession === isDemoSession) {
    return false;
  }
  isDemoApiSession = isDemoSession;
  cachedClient = null;
  hasWarnedAboutMissingDemoUrl = false;
  return true;
};

const resolveBaseUrl = (): string => {
  const standardBaseUrl = process.env.NEXT_PUBLIC_FFH_BACKEND_API_URL;
  if (!standardBaseUrl) {
    throw new Error('NEXT_PUBLIC_FFH_BACKEND_API_URL is not set');
  }
  // MSW fixture mode registers its handlers against the standard base URL,
  // so demo routing must not redirect requests away from them.
  const isMockBackendEnabled =
    process.env.NEXT_PUBLIC_USE_MOCK_FFH_BACKEND === 'true';
  if (isDemoApiSession && !isMockBackendEnabled) {
    const demoBaseUrl = process.env.NEXT_PUBLIC_FFH_DEMO_API_URL;
    if (demoBaseUrl) {
      return demoBaseUrl;
    }
    if (!hasWarnedAboutMissingDemoUrl) {
      hasWarnedAboutMissingDemoUrl = true;
      console.warn(
        'Demo session active but NEXT_PUBLIC_FFH_DEMO_API_URL is unset; falling back to the standard backend.'
      );
    }
  }
  return standardBaseUrl;
};

/**
 * The ffh-backend origin the client calls, for the few requests openapi-fetch
 * cannot make, such as reading a server-sent event stream.
 */
export const ffhBackendBaseUrl = (): string => resolveBaseUrl();

/**
 * Returns the singleton ffh-backend client. Lazy so test environments
 * (MSW handlers, fetch mocks) install themselves before openapi-fetch
 * captures the global fetch reference.
 */
export const ffhClient = (): Client<paths> => {
  if (!cachedClient) {
    cachedClient = createClient<paths>({ baseUrl: resolveBaseUrl() });
  }
  return cachedClient;
};

/**
 * Test-only helper. Resets the cached client and demo routing state so the
 * next ffhClient() call rebuilds it - useful when a test swaps the underlying
 * fetch (e.g. when toggling vitest-fetch-mock off so MSW can intercept).
 */
export const resetFfhClient = (): void => {
  cachedClient = null;
  isDemoApiSession = false;
  hasWarnedAboutMissingDemoUrl = false;
};
